import React, { useState } from "react";
import { formatRut } from "@fdograph/rut-utilities";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  TablePagination,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useNavigate } from "react-router-dom";
import * as locales from "@material-ui/core/locale";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import peopleFill from "@iconify/icons-eva/people-fill";
import arrowForwardFill from "@iconify/icons-eva/arrow-forward-fill";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import BusinessIcon from "@material-ui/icons/Business";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AgregarSocio from "./AgregarSocio";
import MyConfirmationDialog from "../MyConfirmationDialog";
import {
  useGetSociosQuery,
  useGetEmpresasComoSocioQuery,
  useEliminarSocioMutation,
} from "src/services/clientesApi";
import { useSnackbar } from "src/components/snackbar";

// ─── Paleta de colores para avatares ───────────────────────────────────────
const AVATAR_COLORS = [
  "#1976D2",
  "#388E3C",
  "#F57C00",
  "#7B1FA2",
  "#C62828",
  "#00838F",
  "#AD1457",
  "#5D4037",
];

function avatarColor(name) {
  const code = (name?.[0] || "?").toUpperCase().charCodeAt(0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0][0];
}

// ─── Badge de tipo de cliente ───────────────────────────────────────────────
const TIPO_STYLES = {
  SOCIO: { bg: "#E8F5E9", text: "#2E7D32" },
  EMPRESA: { bg: "#E3F2FD", text: "#1565C0" },
  PERSONA: { bg: "#F5F5F5", text: "#616161" },
};

function TipoBadge({ tipo }) {
  if (!tipo) return null;
  const style = TIPO_STYLES[tipo] ?? { bg: "#F5F5F5", text: "#757575" };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.25,
        borderRadius: "6px",
        backgroundColor: style.bg,
        color: style.text,
        fontSize: "0.72rem",
        fontWeight: 600,
        letterSpacing: "0.4px",
        whiteSpace: "nowrap",
      }}
    >
      {tipo}
    </Box>
  );
}

// ─── Estado vacío ──────────────────────────────────────────────────────────
function EmptyState({ message }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 5,
        color: "text.disabled",
        gap: 1,
      }}
    >
      <PersonOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
      <Typography variant="body2" sx={{ color: "text.disabled" }}>
        {message}
      </Typography>
    </Box>
  );
}

// ─── Skeletons de carga ────────────────────────────────────────────────────
function ListSkeletons({ count = 3 }) {
  return (
    <List disablePadding>
      {Array.from({ length: count }).map((_, i) => (
        <ListItem key={i} divider={i < count - 1} sx={{ py: 1.5, px: 3 }}>
          <ListItemAvatar>
            <Skeleton variant="circular" width={42} height={42} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton width="40%" height={18} />}
            secondary={<Skeleton width="28%" height={14} />}
          />
          <Skeleton
            variant="rounded"
            width={64}
            height={22}
            sx={{ borderRadius: "6px" }}
          />
        </ListItem>
      ))}
    </List>
  );
}

// ─── Estilos ───────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 16,
    border: "1px solid #E0E0E0",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    backgroundColor: "#FAFAFA",
    borderBottom: "1px solid #F0F0F0",
  },
  sectionDivider: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: "12px 24px",
    backgroundColor: "#F5F5F5",
    borderTop: "1px solid #EEEEEE",
    borderBottom: "1px solid #EEEEEE",
  },
}));

// ─── Componente principal ──────────────────────────────────────────────────
export default function SociosCliente({
  value,
  setValue,
  cliente,
  setShowMessage,
  setColor,
  setMessage,
}) {
  const classes = useStyles();
  const [locale] = useState("esES");
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [pageEmpresas, setPageEmpresas] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAgregarSocio, setOpenAgregarSocio] = useState(false);
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);

  const skip = value !== 4;

  const { data: socios = [], isLoading: loadingSocios } = useGetSociosQuery(
    { rutCliente: cliente.rut },
    { skip },
  );

  const { data: empresas = [], isLoading: loadingEmpresas } =
    useGetEmpresasComoSocioQuery({ rutSocio: cliente.rut }, { skip });

  const [eliminarSocio] = useEliminarSocioMutation();

  const handleEliminar = async (datosSocio) => {
    try {
      await eliminarSocio({
        refCliente: cliente.rut,
        rutSocio: datosSocio.rutCliente,
      }).unwrap();
      enqueueSnackbar("Socio eliminado correctamente.", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al eliminar el socio.", { variant: "error" });
    } finally {
      setElementoSeleccionado(null);
    }
  };

  const redireccionar = (rutCliente) => {
    setValue(0);
    navigate("/dashboard/clientes/" + rutCliente);
  };

  const sociosPaginados = socios.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const empresasPaginadas = empresas.slice(
    pageEmpresas * rowsPerPage,
    pageEmpresas * rowsPerPage + rowsPerPage,
  );

  if (value !== 4) return null;

  return (
    <>
      <Box className={classes.card}>
        {/* ── Encabezado socios ── */}
        <Box className={classes.sectionHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Icon
              icon={peopleFill}
              style={{ fontSize: 20, color: "#1976D2" }}
            />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "#212121" }}
            >
              Socios
            </Typography>
            {!loadingSocios && (
              <Chip
                size="small"
                label={socios.length}
                sx={{
                  backgroundColor: "#E3F2FD",
                  color: "#1565C0",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  height: 20,
                }}
              />
            )}
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon icon={plusFill} />}
            onClick={() => setOpenAgregarSocio(true)}
            sx={{ borderRadius: "8px" }}
          >
            Agregar Socio
          </Button>
        </Box>

        {/* ── Lista de socios ── */}
        {loadingSocios ? (
          <ListSkeletons count={3} />
        ) : socios.length === 0 ? (
          <EmptyState message="Este cliente no tiene socios registrados" />
        ) : (
          <>
            <List disablePadding>
              {sociosPaginados.map((socio, index) => (
                <ListItem
                  key={socio.rutCliente}
                  divider={index < sociosPaginados.length - 1}
                  sx={{
                    py: 1.5,
                    px: 3,
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: "#FAFAFA" },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: avatarColor(socio.nombreCliente),
                        width: 42,
                        height: 42,
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                      onClick={() => redireccionar(socio.rutCliente)}
                    >
                      {initials(socio.nombreCliente)}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          cursor: "pointer",
                          "&:hover": { color: "#1976D2" },
                          transition: "color 0.15s ease",
                        }}
                        onClick={() => redireccionar(socio.rutCliente)}
                      >
                        {socio.nombreCliente}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: "#9E9E9E" }}>
                        {formatRut(socio.rutCliente)}
                      </Typography>
                    }
                  />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <TipoBadge tipo={socio.tipoCliente} />
                    <Tooltip title="Eliminar socio">
                      <IconButton
                        size="small"
                        onClick={() => setElementoSeleccionado(socio)}
                        sx={{
                          color: "#BDBDBD",
                          "&:hover": {
                            color: "#D32F2F",
                            backgroundColor: "#FFEBEE",
                          },
                          transition: "all 0.15s ease",
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>

            {socios.length > rowsPerPage && (
              <ThemeProvider
                theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
              >
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={socios.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </ThemeProvider>
            )}
          </>
        )}

        {/* ── Sección "Es socio de" ── */}
        {(loadingEmpresas || empresas.length > 0) && (
          <>
            <Box className={classes.sectionDivider}>
              <BusinessIcon sx={{ fontSize: 16, color: "#9E9E9E" }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#757575",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}
              >
                Es socio de
              </Typography>
              <Divider sx={{ flex: 1 }} />
              {!loadingEmpresas && empresas.length > 0 && (
                <Chip
                  size="small"
                  label={empresas.length}
                  sx={{
                    backgroundColor: "#F5F5F5",
                    color: "#757575",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    height: 20,
                  }}
                />
              )}
            </Box>

            {loadingEmpresas ? (
              <ListSkeletons count={2} />
            ) : (
              <>
                <List disablePadding>
                  {empresasPaginadas.map((empresa, index) => (
                    <ListItem
                      key={empresa.rutCliente}
                      divider={index < empresasPaginadas.length - 1}
                      sx={{
                        py: 1.5,
                        px: 3,
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: "#FAFAFA" },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: avatarColor(empresa.nombreCliente),
                            width: 42,
                            height: 42,
                            fontSize: "0.875rem",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                          onClick={() => redireccionar(empresa.rutCliente)}
                        >
                          {initials(empresa.nombreCliente)}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              cursor: "pointer",
                              "&:hover": { color: "#1976D2" },
                              transition: "color 0.15s ease",
                            }}
                            onClick={() => redireccionar(empresa.rutCliente)}
                          >
                            {empresa.nombreCliente}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{ color: "#9E9E9E" }}
                          >
                            {formatRut(empresa.rutCliente)}
                          </Typography>
                        }
                      />

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <TipoBadge tipo={empresa.tipoCliente} />
                        <Tooltip title="Ver empresa">
                          <IconButton
                            size="small"
                            onClick={() => redireccionar(empresa.rutCliente)}
                            sx={{
                              color: "#BDBDBD",
                              "&:hover": {
                                color: "#1976D2",
                                backgroundColor: "#E3F2FD",
                              },
                              transition: "all 0.15s ease",
                            }}
                          >
                            <Icon
                              icon={arrowForwardFill}
                              style={{ fontSize: 16 }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  ))}
                </List>

                {empresas.length > rowsPerPage && (
                  <ThemeProvider
                    theme={(outerTheme) =>
                      createTheme(outerTheme, locales[locale])
                    }
                  >
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      component="div"
                      count={empresas.length}
                      rowsPerPage={rowsPerPage}
                      page={pageEmpresas}
                      onPageChange={(_, p) => setPageEmpresas(p)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPageEmpresas(0);
                      }}
                    />
                  </ThemeProvider>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {openAgregarSocio && (
        <AgregarSocio
          open={openAgregarSocio}
          setOpen={setOpenAgregarSocio}
          refCliente={cliente.rut}
          setShowMessage={setShowMessage}
          setColor={setColor}
          setMessage={setMessage}
        />
      )}

      {elementoSeleccionado && (
        <MyConfirmationDialog
          open={Boolean(elementoSeleccionado)}
          onSubmit={() => handleEliminar(elementoSeleccionado)}
          onClose={() => setElementoSeleccionado(null)}
          title="Eliminar Socio"
          content={`¿Estás seguro de eliminar a ${elementoSeleccionado.nombreCliente} (${formatRut(elementoSeleccionado.rutCliente)}) como socio?`}
        />
      )}
    </>
  );
}
